import {
  DataGrid,
  Ellipsis,
  GridColDef,
  isImage,
  LoadingSpinner,
  MediaCardItem,
  MediaListItem,
  processAttachment,
  StateStore,
  useFormatting,
} from "@msys/ui";
import { Box, Grid, Stack, TablePagination, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link, To, useNavigate } from "react-router-dom";
import {
  ProductListsSortBy,
  ProductListsSorting,
} from "../../../clients/graphqlTypes.js";
import { useCardsNumber } from "../../commons/hooks/useCardsNumber.js";
import { CollectionView } from "../../commons/hooks/useCollectionView.js";
import { NoPropagateBox } from "../../commons/NoPropagateBox.js";
import type { PaginationModel } from "../../commons/pagination/usePaginationParams.js";
import { RESULTS_PER_PAGE_OPTIONS } from "../../constants.js";
import { getPersonLabel } from "../users/helpers.js";
import { ProductListsCollectionView_ProductListFragment } from "./ProductListsCollectionView.generated.js";
import PersonIcon from "@mui/icons-material/Person";

type ProductList = ProductListsCollectionView_ProductListFragment;

interface Props {
  items: ProductList[];
  totalCount: number;
  activeView: CollectionView;
  loading: boolean;
  // When provided, rows/cards render as links (`<a>`, open-in-new-tab). Optional
  // so an embed can rely on `onProductListClick` (or a `RowActions` button)
  // instead. Both may be provided; the link handles navigation, `onProductListClick`
  // handles plain clicks.
  getProductListTo?: (productList: ProductList) => To;
  onProductListClick?: (productList: ProductList) => void;
  // The following are only needed by the full listing (table view + paginated
  // list/gallery). A lightweight embed (e.g. the landing page gallery) can omit
  // them: no pagination footer, no per-row actions, no server sorting.
  paginationModel?: PaginationModel;
  setPaginationModel?: (newPaginationModel: PaginationModel) => void;
  sorting?: ProductListsSorting[];
  setSorting?: (sorting: ProductListsSorting[]) => void;
  stateStore?: StateStore;
  // Per-row actions (rename / duplicate / delete), reused across all views.
  RowActions?: React.ComponentType<{ productList: ProductList }>;
}

/** The list's preview images, image-only and ready for the media components. */
function useProductListImages(productList: ProductList) {
  return React.useMemo(
    () =>
      (productList.images ?? [])
        .map(attachment =>
          processAttachment({ ...attachment, id: attachment.id.toString() })
        )
        .filter(isImage),
    [productList.images]
  );
}

function ProductListInfo({ productList }: { productList: ProductList }) {
  const { getFormattedDate } = useFormatting();
  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="body2" sx={{ color: "grey.600" }}>
        {getFormattedDate(productList.createdAt)}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "grey.600" }}
        component={Stack}
        direction="row"
        spacing={0.5}
      >
        <PersonIcon fontSize="small" />
        <span>{getPersonLabel(productList.author, true)}</span>
      </Typography>
    </Stack>
  );
}

function ProductListCard({
  productList,
  getProductListTo,
  onProductListClick,
  RowActions,
}: {
  productList: ProductList;
  getProductListTo?: (productList: ProductList) => To;
  onProductListClick?: (productList: ProductList) => void;
  RowActions?: React.ComponentType<{ productList: ProductList }>;
}) {
  const { t } = useTranslate(["ProductLists", "Global"]);
  const images = useProductListImages(productList);
  return (
    <MediaCardItem
      key={productList.id}
      // @ts-ignore
      onClick={
        onProductListClick
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onProductListClick(productList);
            }
          : undefined
      }
      {...(getProductListTo
        ? { component: Link, to: getProductListTo(productList) }
        : undefined)}
      title={productList.title}
      description={productList.description}
      descriptionMaxNumberOfLines={1}
      imageUrl={images.map(image => image.url)}
      images={images}
      Info={<ProductListInfo productList={productList} />}
      brand={t(
        "{count, plural, =0 {No products} one {One product} other {# products}}",
        { ns: "ProductLists", count: productList.itemCount }
      )}
      ActionButton={
        RowActions ? (
          <NoPropagateBox>
            <RowActions productList={productList} />
          </NoPropagateBox>
        ) : undefined
      }
    />
  );
}

function ProductListRow({
  productList,
  getProductListTo,
  onProductListClick,
  RowActions,
}: {
  productList: ProductList;
  getProductListTo?: (productList: ProductList) => To;
  onProductListClick?: (productList: ProductList) => void;
  RowActions?: React.ComponentType<{ productList: ProductList }>;
}) {
  const { t } = useTranslate(["ProductLists", "Global"]);
  const images = useProductListImages(productList);
  return (
    <MediaListItem
      key={productList.id}
      // @ts-ignore
      onClick={
        onProductListClick
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onProductListClick(productList);
            }
          : undefined
      }
      {...(getProductListTo
        ? { component: Link, to: getProductListTo(productList) }
        : undefined)}
      title={productList.title}
      description={productList.description}
      descriptionMaxNumberOfLines={1}
      imageUrl={images.map(image => image.url)}
      images={images}
      Info={<ProductListInfo productList={productList} />}
      brand={t(
        "{count, plural, =0 {No products} one {One product} other {# products}}",
        { ns: "ProductLists", count: productList.itemCount }
      )}
      ActionButton={
        RowActions ? (
          <NoPropagateBox>
            <RowActions productList={productList} />
          </NoPropagateBox>
        ) : undefined
      }
    />
  );
}

export function ProductListsCollectionView({
  items,
  totalCount,
  activeView,
  loading,
  paginationModel,
  setPaginationModel,
  sorting,
  setSorting,
  stateStore,
  getProductListTo,
  onProductListClick,
  RowActions,
}: Props) {
  const { t } = useTranslate(["ProductLists", "Global"]);
  const { getFormattedDate, getFormattedInteger } = useFormatting();
  const navigate = useNavigate();
  const { spacing, sizeMeasureRef, columns } = useCardsNumber();

  const tableColumns = React.useMemo<GridColDef<ProductList>[]>(
    () => [
      {
        field: "title",
        headerName: t("Title", { ns: "Global" }),
        flex: 2,
        minWidth: 150,
        sortable: true,
        renderCell: ({ row }) => row.title,
      },
      {
        field: "description",
        headerName: t("Description", { ns: "Global" }),
        flex: 3,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => <Ellipsis>{row.description}</Ellipsis>,
      },
      {
        field: "author",
        headerName: t("Created by", { ns: "Global" }),
        flex: 2,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => row.author.fullname,
      },
      {
        field: "createdAt",
        headerName: t("Created", { ns: "ProductLists" }),
        width: 120,
        sortable: true,
        renderCell: ({ row }) => getFormattedDate(row.createdAt),
      },
      {
        field: "itemCount",
        headerName: t("Items", { ns: "ProductLists" }),
        width: 100,
        sortable: false,
        renderCell: ({ row }) => getFormattedInteger(row.itemCount),
      },
      ...(RowActions
        ? [
            {
              field: "actions",
              headerName: "",
              sortable: false,
              width: 40,
              renderCell: ({ row }) => (
                <NoPropagateBox>
                  <RowActions productList={row} />
                </NoPropagateBox>
              ),
            } satisfies GridColDef<ProductList>,
          ]
        : []),
    ],
    [t, getFormattedDate, getFormattedInteger, RowActions]
  );

  const pagination =
    paginationModel && setPaginationModel ? (
      <TablePagination
        component="div"
        count={totalCount}
        page={paginationModel.page}
        onPageChange={(_event, newPage) => {
          setPaginationModel({
            pageSize: paginationModel.pageSize,
            page: newPage,
          });
        }}
        rowsPerPage={paginationModel.pageSize}
        onRowsPerPageChange={event => {
          setPaginationModel({
            pageSize: parseInt(event.target.value, 10),
            page: 0,
          });
        }}
        labelRowsPerPage={t("Per page:", { ns: "Global" })}
      />
    ) : null;

  const emptyState = (
    <Box sx={{ padding: 2, display: "flex", justifyContent: "center" }}>
      {t("There seems to be nothing here", { ns: "Global" })}
    </Box>
  );

  switch (activeView) {
    case "gallery":
      return (
        <Stack
          ref={sizeMeasureRef}
          direction="column"
          spacing={1}
          sx={{ flex: 1, overflow: "auto", minHeight: 0 }}
        >
          {loading ? (
            <LoadingSpinner />
          ) : !items.length ? (
            emptyState
          ) : (
            <>
              <Box sx={{ overflow: "auto", flex: 1, minHeight: 0 }}>
                <Grid container spacing={spacing}>
                  {items.map(productList => (
                    <Grid key={productList.id} size={columns}>
                      <ProductListCard
                        productList={productList}
                        getProductListTo={getProductListTo}
                        onProductListClick={onProductListClick}
                        RowActions={RowActions}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
              {pagination}
            </>
          )}
        </Stack>
      );

    case "list":
      if (loading) return <LoadingSpinner />;
      if (!items.length) return emptyState;
      return (
        <Stack
          direction="column"
          spacing={1}
          sx={{ flex: 1, overflow: "auto", minHeight: 0 }}
        >
          <Stack
            direction="column"
            spacing={1}
            sx={{ overflow: "auto", flex: 1, minHeight: 0 }}
          >
            {items.map(productList => (
              <ProductListRow
                key={productList.id}
                productList={productList}
                getProductListTo={getProductListTo}
                onProductListClick={onProductListClick}
                RowActions={RowActions}
              />
            ))}
          </Stack>
          {pagination}
        </Stack>
      );

    case "table":
      return (
        <DataGrid<ProductList>
          stateStore={stateStore}
          loading={loading}
          hideFooter={totalCount === 0}
          columns={tableColumns}
          rows={items}
          onRowClick={({ row }) => {
            if (onProductListClick) {
              onProductListClick(row);
            } else if (getProductListTo) {
              navigate(getProductListTo(row));
            }
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={newPaginationModel => {
            setPaginationModel?.(newPaginationModel);
          }}
          pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
          rowCount={totalCount}
          sortModel={(sorting ?? []).map(s => ({
            field: s.column,
            sort: s.direction,
          }))}
          onSortModelChange={newModel => {
            setSorting?.(
              newModel.map(({ field, sort }) => ({
                column: field as ProductListsSortBy,
                direction: sort ?? "asc",
              }))
            );
          }}
          localeText={{
            noRowsLabel: t("There are no items to display", { ns: "Global" }),
            paginationRowsPerPage: t("Rows per page", { ns: "Global" }),
          }}
        />
      );
  }

  return null;
}
